import React, { Dispatch, SetStateAction, useMemo } from 'react'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { SelectControlsRowItem } from 'src/components/Reusable/Forms/MultiSelectFromComponents/SelectControlsRowItem'
import { NoRecommendedTemplates } from 'src/components/ModalForms/Frameworks/AddFramework/NoRecommendedTemplates'
import { Grid } from 'src/components/Reusable/Grid'
import { Spinner } from 'src/Throbber'
import { FlexAlign, FlexColumn } from 'src/components/Reusable/Flex'
import { SelectedCountSpan } from 'src/components/ModalForms/ModalForm.styles'
import { getPluralization } from 'src/Utils/globalHelpers'
import { usePollEvidenceControlMappings } from '../evidence.hooks'

const GRID_TEMPLATE_COLUMNS =
  'min-content minmax(min-content, auto) minmax(min-content, auto) minmax(min-content, 1fr)'

type SuggestedControlsTabProps = {
  evidenceIds: string[]
  selectedControls: string[]
  setSelectedControls: Dispatch<SetStateAction<string[]>>
  isSubmitting: boolean
  existingControlIds?: string[]
}

export const SuggestedControlsTabLoading = (): JSX.Element => {
  return (
    <FlexColumn justify={FlexAlign.CENTER} $padding={32} gap={12}>
      <Spinner color="primary" size="xl" />
      <span>Analyzing your evidence to find suggested controls&hellip;</span>
    </FlexColumn>
  )
}

export const SuggestedControlsTab = ({
  evidenceIds,
  selectedControls,
  setSelectedControls,
  isSubmitting,
  existingControlIds,
}: SuggestedControlsTabProps): JSX.Element => {
  const { mappings, isPolling, isLoading } = usePollEvidenceControlMappings({
    evidenceIds,
  })

  const suggestedControls = useMemo((): Control[] => {
    const seen = new Set<string>()
    const result: Control[] = []
    for (const mapping of mappings) {
      for (const control of mapping.getSuggestedControlsList()) {
        const id = control.getId()
        if (!seen.has(id) && !existingControlIds?.includes(id)) {
          seen.add(id)
          result.push(control)
        }
      }
    }
    return result
  }, [mappings, existingControlIds])

  const showLoading = isLoading || isPolling

  if (showLoading) {
    return <SuggestedControlsTabLoading />
  }

  if (suggestedControls.length === 0) {
    return <NoRecommendedTemplates templateType="suggested controls" />
  }

  return (
    <>
      <SelectedCountSpan>
        {`${selectedControls.length} ${getPluralization(
          'control',
          selectedControls.length,
        )} selected`}
      </SelectedCountSpan>
      <div
        style={{
          maxHeight: '350px',
          overflowY: 'auto',
          width: '100%',
          marginTop: '25px',
        }}
      >
        <Grid gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
          {suggestedControls.map((control) => (
            <SelectControlsRowItem
              key={control.getId()}
              control={control}
              setSelectedControlIds={setSelectedControls}
              selectedControlIds={selectedControls}
              disabled={isSubmitting}
            />
          ))}
        </Grid>
      </div>
    </>
  )
}
